import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

import { Bell, Settings, Trash2, ChevronLeft } from "lucide-react";

import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import { Checkbox } from "js/components/ui/checkbox";
import { Label } from "js/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";

import { Notification, NotificationSettings } from "js/components/types";

import {
  useGetNotifications,
  useGetNotificationSettings,
  useMarkNotificationAsRead,
  useDeleteNotification,
  useUpdateNotificationSettings,
} from "../../hooks/useNotifications";
import { queryKeys } from "../../hooks/queryKeys";
import { API } from "../../constants/api";
import { getTimeElapsed } from "../../utils/utils";

const NotificationBell: React.FC = () => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [localSettings, setLocalSettings] = useState<NotificationSettings>({
    email_notifications: false,
    push_notifications: false,
  });

  const { userData } = useSelector((state: any) => state.entities.user);
  const queryClient = useQueryClient();

  // Query hooks
  const { data: notifications = [], refetch: refetchNotifications } = useGetNotifications();
  const { data: notificationSettings } = useGetNotificationSettings();

  // Mutation hooks
  const markAsReadMutation = useMarkNotificationAsRead();
  const deleteMutation = useDeleteNotification();
  const updateSettingsMutation = useUpdateNotificationSettings();

  // Sync local settings with query data
  useEffect(() => {
    if (notificationSettings) {
      setLocalSettings(notificationSettings);
    }
  }, [notificationSettings]);

  const handleOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    if (!open) {
      setShowSettings(false);
    }
  };

  const handleSettingsToggle = () => {
    setShowSettings(!showSettings);
  };

  const handleNotificationClick = (notificationId: string) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleNotificationDelete = (notificationId: string) => {
    deleteMutation.mutate(notificationId);
  };

  const handleSettingsSave = () => {
    updateSettingsMutation.mutate(localSettings, {
      onSuccess: () => {
        setShowSettings(false);
      },
    });
  };

  const handlePopoverOpen = () => {
    if (!popoverOpen) {
      refetchNotifications();
    }
  };

  useEffect(() => {
    if (!userData?.id) return;

    const eventSource = new EventSource(
      `${API}/v1/notifications/${userData.id}/events`,
    );

    eventSource.onmessage = (event) => {
      const newEvent = JSON.parse(event.data);
      newEvent.timeSince = getTimeElapsed(newEvent.created_at);
      // Update TanStack Query cache with new notification from SSE
      queryClient.setQueryData<Notification[]>(
        queryKeys.notifications.list(),
        (old = []) => [newEvent, ...old],
      );
    };

    return () => {
      eventSource.close();
    };
  }, [userData, queryClient]);

  const unreadCount = notifications.filter((n) => !n.seen).length;

  return (
    <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={handlePopoverOpen}
          className="relative bg-white hover:bg-white rounded-full"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="end">
        <div className="flex flex-col max-h-[600px]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            {showSettings && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(false)}
                className="h-8 w-8 -ml-2"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <h3 className="font-semibold text-base">
              {showSettings ? "Configuración" : "Notificaciones"}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSettingsToggle}
              className="h-8 w-8"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          {showSettings ? (
            <div className="p-4 space-y-4">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="push_notifications"
                  checked={localSettings.push_notifications}
                  onCheckedChange={(checked) =>
                    setLocalSettings({
                      ...localSettings,
                      push_notifications: checked as boolean,
                    })
                  }
                />
                <Label
                  htmlFor="push_notifications"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Notificaciones Push
                </Label>
              </div>
              <Button
                onClick={handleSettingsSave}
                className="w-full"
                loading={updateSettingsMutation.isPending}
              >
                Guardar
              </Button>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[520px]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No hay notificaciones
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="relative p-3 hover:bg-gray-50 transition-colors cursor-pointer border-l-4"
                      style={{
                        borderLeftColor: notification.title
                          .toLowerCase()
                          .includes("bloqueado")
                          ? "red"
                          : notification.seen
                            ? "#e5e7eb"
                            : "#3b82f6",
                        backgroundColor: notification.seen
                          ? "transparent"
                          : notification.title
                              .toLowerCase()
                              .includes("bloqueado")
                            ? "#fef2f2"
                            : "#eff6ff",
                      }}
                      onClick={
                        notification.seen
                          ? undefined
                          : () => handleNotificationClick(notification.id)
                      }
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <h4
                            className="font-semibold text-sm mb-1 truncate"
                            style={{
                              color: notification.seen ? "#6b7280" : "inherit",
                            }}
                          >
                            {notification.title}
                          </h4>
                          <p
                            className="text-xs mb-2 line-clamp-2"
                            style={{
                              color: notification.seen ? "#9ca3af" : "#374151",
                            }}
                          >
                            {notification.body}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {notification.timeSince}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationDelete(notification.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
